import {useState, useEffect, useRef} from 'preact/hooks'
import {FilterSidebar} from './components/FilterSidebar'
import {ResultsGrid} from './components/ResultsGrid'
import {SearchWidget} from './components/SearchWidget'
import {
    Travel,
    SearchFilters,
    FilterOptions,
    DropdownPayload,
    Termin,
    ExtIdItem,
    GrundsortierungProps
} from './types/travel'
import './App.css'
import {FilterMobile} from "./components/FilterMobile.tsx";
import {trackEvent} from "./utils/analytics.ts";
import {toYearMonthDayTrackingString, truncateToMaxBytes} from "./utils/general.ts";
import {deleteCookie, setCookie} from "./utils/cookies.ts";
import {parseQueryStringToFilters, validFilterKeys} from "./utils/queryString.ts";

interface AppProps {
    viewType?: string
    contentInfusion?: string
    toolTipTexts?: string
    prefillFilters?: string
    kuratierteReisen?: string
    searchRedirects?: string
    grundsortierung?: string
    alternativeTexts?: string
    pageSize?: number
    enableQueryStringSync?: boolean
    showSearchUrl?: boolean
}

// Environment-dependent API URL configuration
const getApiUrl = (): string => {
    // @ts-ignore - Vite's import.meta.env
    const mode = import.meta.env?.MODE || 'development'

    switch (mode) {
        case 'development':
            return 'http://weltweitwandern.at.localhost/api/meili/search.php'
        case 'stage':
            return 'https://wandern.epsimec.dev/api/meili/search.php'
        case 'production':
            return 'https://www.weltweitwandern.at/api/meili/search.php'
        default:
            return 'http://weltweitwandern.at.localhost/api/meili/search.php'
    }
}

// Function to update query string when filters change
const updateQueryStringFromFilters = (filters: SearchFilters) => {
    const params = new URLSearchParams(window.location.search)

    // Remove existing filter parameters
    validFilterKeys.forEach(key => {
        params.delete(key)
    })

    // Add current filter values
    Object.entries(filters).forEach(([key, value]) => {
        if (value && validFilterKeys.includes(key)) {
            if (Array.isArray(value) && value.length > 0) {
                params.set(key, value.join(','))
            } else if (typeof value === 'string' && value.trim() !== '') {
                params.set(key, value)
            }
        }
    })

    // Update URL without reloading the page
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`
    window.history.replaceState({}, '', newUrl)
}

const parseQueryStringToDateToggl = (): boolean => {
    const params = new URLSearchParams(window.location.search)
    const value = params.get('termine')
    return value !== 'true';
}

export function App(props: AppProps) {

    let prefillFiltersData = []
    if(props.prefillFilters) {
        prefillFiltersData = JSON.parse(props.prefillFilters);
    }

    const PAGE_SIZE = props.pageSize || 11

    // Query-string sync is opt-in per block instance (ACF field), so the same block
    // can hold its state purely in-app on pages other than the main search page.

    //TODO 1213989773257663 use the first line below instead of syncQueryString true
    const syncQueryString = props.viewType !== 'frontpage' && !!props.enableQueryStringSync
    //const syncQueryString = true;

    const [filters, setFilters] = useState<SearchFilters>(props.viewType !== 'frontpage' ? parseQueryStringToFilters(prefillFiltersData) : {})
    const [results, setResults] = useState<Travel[]>([])
    const [termine, setTermine] = useState<Termin[]>([])
    const [extIdItems, setExtIdItems] = useState<ExtIdItem[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [isExtIdsLoaded, setIsExtIdsLoaded] = useState<boolean>(false)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)
    const [isFilterOverlayOpen, setIsFilterOverlayOpen] = useState<boolean>(false)
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({})
    const [pagination, setPagination] = useState({ page: 1, terminPage: 1, limit: PAGE_SIZE, total: 0, terminTotal: 0})
    const [sortBy, setSortBy] = useState<string>('customsort')
    const [gridViewState, setGridViewState] = useState(parseQueryStringToDateToggl());
    const [dateTogglState, setDateTogglState] = useState(true);
    const resultsRef = useRef<HTMLDivElement>(null);
    const [mobileHeadingTag, setMobileHeadingTag] = useState<'h1' | 'h2'>('h2');

    useEffect(() => {
        if (!document.querySelector('h1')) {
            setMobileHeadingTag('h1');
        }
    }, []);

    //tracking
    const isFirstLoad = useRef(true);
    const previousDestination = useRef<string[] | undefined>(undefined);
    const [searchCategory, ] = useState( 'reisekalender')
    /*const hasUrlParameters = (): boolean => {
        const params = new URLSearchParams(window.location.search)
        return validFilterKeys.some(key => params.has(key))
    }
    const [searchCategory, ] = useState( hasUrlParameters() ? 'redirect' : 'reisekalender');*/

    let contentInfusionData = [];
    if(props.contentInfusion) {
        contentInfusionData = JSON.parse(props.contentInfusion);
    }

    let toolTipTextsData  = [];
    if(props.toolTipTexts) {
        toolTipTextsData = JSON.parse(props.toolTipTexts);
    }

    let kuratierteReisenData = []
    if(props.kuratierteReisen && props.kuratierteReisen.length > 0 && props.viewType === 'frontpage') {
        kuratierteReisenData = JSON.parse(props.kuratierteReisen).kuratierte_reisen;
    }

    let searchRedirects = []
    if(props.searchRedirects) {
        searchRedirects = JSON.parse(props.searchRedirects);
    }

    const grundsortierung: GrundsortierungProps = props.grundsortierung
        ? JSON.parse(props.grundsortierung)
        : {}

    const alternativeTexts: Record<string, string> = props.alternativeTexts
        ? JSON.parse(props.alternativeTexts)
        : {}

    // Fetch results and filter options from search API
    const fetchResultsAndFilters = async (
        page: number = 1,
        terminPage: number = 1,
        limit: number = PAGE_SIZE,
        appendResults: boolean = false,
        appendTerminResults: boolean = false,
        trackGAEvent: boolean = true
    ) => {
        try {
            if (appendResults || appendTerminResults) {
                setLoadingMore(true)
            } else {
                setLoading(true)
            }

            const response = await fetch(getApiUrl(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    filters: filters,
                    page: page,
                    terminPage: terminPage,
                    limit: limit,
                    sortBy: sortBy,
                    loadExtids: !isExtIdsLoaded
                })
            })

            const data = await response.json()

            if (data.error) {
                console.error('Search API error:', data.error)
                return
            }

            if(!isExtIdsLoaded && data.extids && Array.isArray(data.extids)) {
                setExtIdItems(data.extids)
                setIsExtIdsLoaded(true)
            }

            // Update results - append or replace based on mode
            if (appendResults) {
                setResults(prevResults => [...prevResults, ...(data.results || [])])
            } else {
                setResults(data.results || [])
            }

            if(appendTerminResults) {
                setTermine(prevResults => [...prevResults, ...(data.termine || [])])
            } else {
                setTermine(data.termine || [])
            }

            // Update pagination
            setPagination({
                page: data.page || 1,
                terminPage: data.terminPage || 1,
                limit: data.limit || PAGE_SIZE,
                total: data.total || 0,
                terminTotal: data.terminTotal || 0
            })

            //Event Tracking Data
            let trackDestinationData = filters.destination ?? [];
            let trackDestinationString = '';
            if(trackDestinationData.length > 0) {
                trackDestinationString = trackDestinationData.join(',');
            }

            let trackSearchDates = 'egal';
            let trackDestinationYearMonth = filters.yearmonth ?? [];
            if(trackDestinationYearMonth.length > 0) {

                trackSearchDates = trackDestinationYearMonth.map((item) => toYearMonthDayTrackingString(item))
                    .join(',');

            }

            let trackDestinationStart = filters.start ?? '';
            let trackDestinationEnd = filters.end ?? '';
            if(trackDestinationStart.length > 0 && trackDestinationEnd.length > 0) {
                const trackingStart = toYearMonthDayTrackingString(trackDestinationStart);
                const trackingEnd = toYearMonthDayTrackingString(trackDestinationEnd);
                trackSearchDates = `${trackingStart} bis ${trackingEnd}`;
            }

            if(trackGAEvent) {
                trackEvent('view_search_results', {
                    search_term: truncateToMaxBytes(trackDestinationString),
                    search_category: searchCategory,
                    search_datetime: truncateToMaxBytes(trackSearchDates),
                    search_results: data.total || 0
                })

            } else {
                console.log('no tracking')
            }

            //Event Tracking set cookie
            const tracking_options = {
                type: 'buchen',
                outbound: false,
                search_term: truncateToMaxBytes(trackDestinationString),
                search_category: 'reisekalender',
                search_datetime: truncateToMaxBytes(trackSearchDates),
                search_results: data.total || 0
            }
            deleteCookie('ep-search-track-reisekalender');
            setCookie('ep-search-track-reisekalender', JSON.stringify(tracking_options), 1);


            // Update filter options from facet distribution
            if (data.facetDistribution) {
                setFilterOptions({
                    destination: data.facetDistribution.destination || [],
                    difficulty: data.facetDistribution.difficulty || [],
                    reiseart: data.facetDistribution.reiseart || [],
                    interesse: data.facetDistribution.interesse || [],
                    anreiseart: data.facetDistribution.anreiseart || [],
                    duration: data.facetDistribution.duration || [],
                    yearmonth: data.facetDistribution.yearmonth || [],
                    unterkunft: data.facetDistribution.unterkunft || [],
                    veranstalter: data.facetDistribution.veranstalter || [],
                    standortwechsel: data.facetDistribution.standortwechsel || [],
                    empfehlung: data.facetDistribution.empfehlung || [],
                    priceRange: data.priceRange || { min: 0, max: 5000 },
                    // Add other filter categories as they become available
                })
            }

        } catch (error) {
            console.error('Failed to fetch search results:', error)
            setResults([])
        } finally {
            setLoading(false)
            setLoadingMore(false)

            // Scroll to results section after results are loaded (but not when loading more)
            if (!appendResults && !appendTerminResults) {
                setTimeout(() => {
                    if (resultsRef.current) {
                        // Check if any travel tiles are visible in viewport
                        const tiles = resultsRef.current.querySelectorAll<HTMLElement>(".journey-tile, .travel-date");

                        const anyTileVisible = tiles.length > 0 && Array.from(tiles).some(el => {
                            const rect = el.getBoundingClientRect();
                            const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
                            return visibleHeight >= rect.height * 0.5;
                        });

                        /*console.log('Scroll check:', {
                            tilesCount: tiles.length,
                            anyTileVisible: anyTileVisible,
                            currentScroll: window.scrollY
                        });*/

                        // Only scroll if NO tiles are visible
                        if (
                            window.location.href.includes('suche') &&
                            !anyTileVisible &&
                            tiles.length > 0
                        ) {
                            const rect = resultsRef.current.getBoundingClientRect();
                            const elementTop = rect.top + window.scrollY;

                            //console.log('No tiles visible, scrolling to results section at:', elementTop - 20);
                            window.scrollTo({
                                top: elementTop - 120,
                                behavior: 'smooth'
                            });
                        } else {
                            //console.log('Tiles are visible or no tiles yet, NOT scrolling');
                        }
                    } else {
                        //console.log('resultsRef.current is null');
                    }
                }, 500);
            }
        }
    }

    // Handle loading more results
    const handleLoadMore = () => {
        const nextPage = pagination.page + 1
        //auto reset termin page to 1
        fetchResultsAndFilters(nextPage, 1, pagination.limit, true, false, false)
    }

    const handleLoadMoreTermine = () => {
        const nextTerminPage = pagination.terminPage + 1
        //auto reset reise page to 1
        fetchResultsAndFilters(1, nextTerminPage, pagination.limit, false, true, false)
    }

    // Check if there are more results to load
    const hasMoreResults = results.length < pagination.total

    // Check if there are more results to load
    const hasMoreTerminResults = termine.length < pagination.terminTotal

    // Hide filter sections that have no active selection when there are no results
    const hideEmptyFilters = pagination.total === 0 && !loading

    // Handle sort changes
    const handleSortChange = (payload: DropdownPayload) => {
        if (payload.value) {
            setSortBy(payload.value)
        }
    }

    //initial load of elements
    useEffect(() => {
        if(Object.keys(filters).length === 0) {
            fetchResultsAndFilters(1, 1, PAGE_SIZE, false, false, true)
            isFirstLoad.current = false
        }
    }, []);

    // Effect for filter changes - reset to page 1 and clear results
    useEffect(() => {
        // Skip initial render to avoid duplicate call with initial useEffect
        if (results.length > 0 || Object.keys(filters).length > 0) {

            const currentDestination = filters.destination;
            const hasDestinationChanged = JSON.stringify(previousDestination.current) !== JSON.stringify(currentDestination);
            const shouldTrack = hasDestinationChanged;
            previousDestination.current = currentDestination;

            fetchResultsAndFilters(1, 1, pagination.limit, false, false, shouldTrack)
            isFirstLoad.current = false
        }
    }, [filters, sortBy])

    // Effect to update query string when filters change
    useEffect(() => {
        if(syncQueryString) {
            updateQueryStringFromFilters(filters)
        }
    }, [filters])

    // Effect to sync gridViewState to the "termine" query param
    useEffect(() => {
        if (syncQueryString) {
            const params = new URLSearchParams(window.location.search)
            if (!gridViewState) {
                params.set('termine', 'true')
            } else {
                params.delete('termine')
            }
            const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`
            window.history.replaceState({}, '', newUrl)
        }
    }, [gridViewState])

    return (
        <div className="app-container text-theme-black">
            {props.viewType === "frontpage" && (
                <main className="
                    app-container-front
                    relative lg:absolute
                    top-full lg:top-0
                    lg:inset-0
                    lg:flex lg:items-center lg:justify-center
                    lg:mt-[97px] lg:mx-auto  ">
                    <SearchWidget
                        filterOptions={filterOptions || {}}
                        onFiltersChange={setFilters}
                        filters={filters}
                        gridViewState={dateTogglState}
                        setGridViewState={setDateTogglState}
                        kuratierteReisen={kuratierteReisenData || []}
                        extIdItems={extIdItems}
                        searchRedirects={searchRedirects || []}
                    />
                </main>
            )}

            {props.viewType !== "frontpage" && (
                <main className="app-main www-inner-main pt-5 pb-[50px] md:pt-5 md:pb-[70px] lg:pt-[50px] lg:pb-25">

                    {(() => {
                        const t = pagination.total;
                        const tt = pagination.terminTotal;
                        const singular = (n: number, s: string, p: string) => n > 1 ? p : s;
                        const reisenLabel = alternativeTexts?.reisen_gefunden
                            ? alternativeTexts.reisen_gefunden.replace('%s', String(t))
                            : `${t} ${singular(t, 'Reise', 'Reisen')} gefunden`;
                        const termineLabel = alternativeTexts?.termine_gefunden
                            ? alternativeTexts.termine_gefunden.replace('%s', String(tt))
                            : `${tt} ${singular(tt, 'Termin', 'Termine')} gefunden`;
                        const mobileText = (!gridViewState && tt === 0)
                            ? 'Keine Reise gefunden'
                            : gridViewState ? reisenLabel : termineLabel;
                        const MobileHeading = mobileHeadingTag as 'h1' | 'h2';
                        const MobilHeadingPaddingTop = MobileHeading === 'h1' ? 'pt-0' : 'pt-5';
                        return <MobileHeading className={`${MobilHeadingPaddingTop} pb-10 leading-none block lg:hidden like-h1`}>{mobileText}</MobileHeading>;
                    })()}

                    <aside className="app-sidebar">
                        <FilterSidebar
                            gridViewState={dateTogglState}
                            setGridViewState={setDateTogglState}
                            filters={filters}
                            onFiltersChange={setFilters}
                            filterOptions={filterOptions || {}}
                            toolTipTexts={toolTipTextsData}
                            prefillFilters={prefillFiltersData}
                            kuratierteReisen={kuratierteReisenData || []}
                            extIdItems={extIdItems || []}
                            searchRedirects={searchRedirects || []}
                            grundsortierung={grundsortierung}
                            hideEmptyFilters={hideEmptyFilters}
                        />
                    </aside>

                    <div className="app-mobile-filter">
                        <FilterMobile
                            gridViewState={dateTogglState}
                            setGridViewState={setDateTogglState}
                            currentGridToggleViewState={gridViewState}
                            filters={filters}
                            onFiltersChange={setFilters}
                            onSortChange={handleSortChange}
                            total={pagination.total}
                            isFilterOverlayOpen={isFilterOverlayOpen}
                            setIsFilterOverlayOpen={setIsFilterOverlayOpen}
                            filterOptions={filterOptions || {}}
                            toolTipTexts={toolTipTextsData}
                            prefillFilters={prefillFiltersData}
                            extIdItems={extIdItems || []}
                            grundsortierung={grundsortierung}
                            hideEmptyFilters={hideEmptyFilters}
                        />
                    </div>

                    <section className="app-content" ref={resultsRef}>
                        <ResultsGrid
                            gridViewState={gridViewState}
                            setGridViewState={setGridViewState}
                            results={results}
                            termine={termine}
                            terminTotal={pagination.terminTotal}
                            loading={loading}
                            loadingMore={loadingMore}
                            filters={filters}
                            onFiltersChange={setFilters}
                            onSortChange={handleSortChange}
                            isFilterOverlayOpen={isFilterOverlayOpen}
                            setIsFilterOverlayOpen={setIsFilterOverlayOpen}
                            total={pagination.total}
                            limit={pagination.limit}
                            contentInfusion={contentInfusionData}
                            onLoadMore={handleLoadMore}
                            onLoadMoreTermine={handleLoadMoreTermine}
                            hasMoreResults={hasMoreResults}
                            hasMoreTerminResults={hasMoreTerminResults}
                            currentSortBy={sortBy}
                            toolTipTexts={toolTipTextsData}
                            prefillFilters={prefillFiltersData}
                            alternativeTexts={alternativeTexts}
                            showSearchUrl={!!props.showSearchUrl}
                        />
                    </section>

                </main>
            )}
        </div>
    )
}
